/**
 * TaskGroupsAdmin.tsx — Admin > Tasks > Task Groups.
 *
 * The corporate dashboard's Task Groups page for the signed-in account, in the
 * Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Task Group Name (with corp's "Required" badge), Trigger (its coloured
 *     badge and where it adds tasks — red "missing" when a stage / status
 *     trigger has none), Tasks (how many, of which types) and Filters (how many
 *     are set);
 *   - search by name or trigger; filter by trigger and by task kind (human /
 *     automatic); Show deleted;
 *   - "Add task group" top right; a click on a row opens it; Edit / Delete,
 *     or Restore on a deleted one;
 *   - the form is TaskGroupFormModal; a task in it opens the task's own form.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column, and no wording that invites
 * a change; a group, and a task in it, still opens read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { ListFilterBar } from '@/components/admin/ListFilterBar';
import { TaskFormModal } from '@/components/admin/TaskFormModal';
import { TaskGroupFormModal } from '@/components/admin/TaskGroupFormModal';
import { itemTaskType } from '@/components/admin/TaskItemCard';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  APPT_STATUS_LABELS,
  EVENT_TRIGGERS,
  FILTER_KEYS,
  TRIGGER_TYPE_BADGE,
  TRIGGER_TYPE_LABELS,
  csvIds,
  deleteTaskGroup,
  getTaskGroupFilterOptions,
  listAdminTaskGroups,
  restoreTaskGroup,
  type AdminTaskGroup,
  type TaskGroupFilterOptions,
} from '@/services/taskGroupsAdmin.service';
import { listAdminTasks, taskTypeLabel, type AdminTask } from '@/services/tasksAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Task Group Name', className: 'w-[28%]', sortable: true },
  { key: 'trigger', header: 'Trigger', sortable: true },
  { key: 'tasks', header: 'Tasks' },
  { key: 'filters', header: 'Filters', className: 'w-20' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function groupName(g: AdminTaskGroup): string {
  return g.name.trim() || `Task group #${g.taskGroupID}`;
}

function triggerLabel(g: AdminTaskGroup): string {
  return TRIGGER_TYPE_LABELS[g.triggerType] ?? 'Unknown';
}

const SORT: ListSortCompare<AdminTaskGroup> = {
  name: (a, b) => compareText(groupName(a), groupName(b)),
  trigger: (a, b) => compareText(triggerLabel(a), triggerLabel(b)),
};

/** How many filters are set (corp leaves "Other" out of the count). */
function filterCount(g: AdminTaskGroup): number {
  return FILTER_KEYS.filter((k) => k !== 'extraFilters' && csvIds(g.filters[k]).length > 0).length;
}

/** Where the trigger adds tasks: the stages, statuses or appointment stages; '' when none. */
export function triggerWhere(g: AdminTaskGroup, opts: TaskGroupFilterOptions | null): string {
  const name = (list: { id: string; name: string }[] | undefined, id: number) => list?.find((o) => o.id === String(id))?.name;
  if (g.triggerType === 0) {
    return g.stageTriggers
      .filter((s) => !s.remove)
      .map((s) => name(opts?.stages, s.value) ?? (s.label || `Stage ${s.value}`))
      .join(', ');
  }
  if (g.triggerType === 5) {
    return g.statusTriggers
      .filter((s) => !s.remove)
      .map((s) => s.label || name(opts?.stageSubs, s.value) || `Status ${s.value}`)
      .join(', ');
  }
  if (g.triggerType === 1) {
    return g.apptTriggers
      .filter((s) => !s.remove)
      .map((s) => APPT_STATUS_LABELS[s.value] ?? `Status ${s.value}`)
      .join(', ');
  }
  return '';
}

function tasksSummary(g: AdminTaskGroup, tasks: AdminTask[]): string {
  if (g.items.length === 0) return 'No tasks';
  const kinds = [...new Set(g.items.map((i) => taskTypeLabel(itemTaskType(i, tasks))))];
  return `${g.items.length} task${g.items.length === 1 ? '' : 's'}: ${kinds.join(', ')}`;
}

const KIND_OPTIONS = [
  { value: 'human', label: 'Has Human Tasks' },
  { value: 'auto', label: 'Has Auto Tasks' },
];

export function TaskGroupsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [groups, setGroups] = useState<AdminTaskGroup[]>([]);
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [opts, setOpts] = useState<TaskGroupFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [triggers, setTriggers] = useState<string[]>([]);
  const [kinds, setKinds] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminTaskGroup | 'new' | null>(null);
  const [editingTaskID, setEditingTaskID] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<AdminTaskGroup | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [groupList, taskList] = await Promise.all([listAdminTaskGroups(), listAdminTasks().catch(() => [] as AdminTask[])]);
      setGroups(groupList);
      setTasks(taskList);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load task groups'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(() => {
    listAdminTasks()
      .then(setTasks)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    void load();
    getTaskGroupFilterOptions()
      .then(setOpts)
      .catch(() => setOpts(null));
  }, [load]);

  const triggerOptions = useMemo(
    () =>
      [...new Set(groups.map((g) => g.triggerType))]
        .sort((a, b) => a - b)
        .map((type) => ({ value: String(type), label: TRIGGER_TYPE_LABELS[type] ?? `Trigger ${type}` })),
    [groups],
  );

  const query = search.trim().toLowerCase();
  const filtering = query !== '' || triggers.length > 0 || kinds.length > 0;
  const rows = useMemo(
    () =>
      groups.filter((g) => {
        if (!showDeleted && !g.active) return false;
        if (query && ![groupName(g), triggerLabel(g), triggerWhere(g, opts)].some((s) => s.toLowerCase().includes(query))) return false;
        if (triggers.length > 0 && !triggers.includes(String(g.triggerType))) return false;
        if (kinds.length > 0) {
          // An item assigned to Automatic ('ftp') is done by the system; anything else by a person or team.
          const hasAuto = g.items.some((i) => i.assignedToID === 'ftp');
          const hasHuman = g.items.some((i) => i.assignedToID !== 'ftp');
          if (!((kinds.includes('auto') && hasAuto) || (kinds.includes('human') && hasHuman))) return false;
        }
        return true;
      }),
    [groups, showDeleted, query, triggers, kinds, opts],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, triggers, kinds, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { taskGroupID } = deleting;
    const name = groupName(deleting);
    try {
      await deleteTaskGroup(taskGroupID);
      setGroups((prev) => prev.map((g) => (g.taskGroupID === taskGroupID ? { ...g, active: false } : g)));
      toast.success(`"${name}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The task group could not be deleted.'));
    }
  }

  async function handleRestore(g: AdminTaskGroup) {
    try {
      await restoreTaskGroup(g.taskGroupID);
      setGroups((prev) => prev.map((x) => (x.taskGroupID === g.taskGroupID ? { ...x, active: true } : x)));
      toast.success('Task group restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The task group could not be restored.'));
    }
  }

  const editingTask = editingTaskID === null ? null : tasks.find((t) => t.taskID === editingTaskID) ?? null;

  return (
    <AdminListPage
      title="Task Groups"
      subtitle={locked ? 'Task group triggers, filters, and task items.' : 'Manage task group triggers, filters, and task items.'}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add task group
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search task groups..."
          searchLabel="Search task groups"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="task-groups-show-deleted"
        >
          <ListFilterBar
            filters={[
              {
                id: 'task-groups-trigger-filter',
                label: 'Filter by trigger',
                placeholder: 'All Triggers',
                searchPlaceholder: 'Search triggers...',
                emptyMessage: 'No triggers found.',
                options: triggerOptions,
                selected: triggers,
                onChange: setTriggers,
              },
              {
                id: 'task-groups-kind-filter',
                label: 'Filter by task kind',
                placeholder: 'Any Task Kind',
                searchPlaceholder: 'Search...',
                emptyMessage: 'No options.',
                options: KIND_OPTIONS,
                selected: kinds,
                onChange: setKinds,
              },
            ]}
          />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading task groups…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={filtering ? 'No task groups found' : 'No task groups yet'}
          hint={filtering ? 'Try adjusting your search or filters.' : locked ? undefined : 'Add a task group to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Task groups" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((g) => {
              const deleted = !g.active;
              const name = groupName(g);
              const where = triggerWhere(g, opts);
              const count = filterCount(g);
              return (
                <tr key={g.taskGroupID} data-task-group-id={g.taskGroupID} className={adminRowClass({ deleted, clickable: true })} onClick={() => setEditing(g)}>
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName
                      deleted={deleted}
                      nameTestId="task-group-name"
                      suffix={
                        g.required ? (
                          <Badge variant="info" size="sm">
                            Required
                          </Badge>
                        ) : undefined
                      }
                    >
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex min-w-0 flex-col gap-1">
                      <span
                        className={cn(
                          'inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                          TRIGGER_TYPE_BADGE[g.triggerType] ?? 'bg-slate-100 text-slate-600 border-slate-200',
                        )}
                      >
                        {triggerLabel(g)}
                      </span>
                      {EVENT_TRIGGERS.has(g.triggerType) ? null : where ? (
                        <span className="truncate text-xs text-slate-500" title={where}>
                          {where}
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-red-600">missing</span>
                      )}
                    </div>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-xs text-slate-600')}>{tasksSummary(g, tasks)}</td>
                  <td className={ADMIN_TD_CLASS}>{count > 0 ? <OutlineBadge>{count}</OutlineBadge> : <span className="text-xs text-slate-400">—</span>}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(g)} />
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(g)} />
                        ) : (
                          <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(g)} />
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['task group', 'task groups']} />
        </>
      )}

      {editing && (
        <TaskGroupFormModal
          key={editing === 'new' ? 'new' : editing.taskGroupID}
          taskGroupID={editing === 'new' ? null : editing.taskGroupID}
          tasks={tasks}
          onEditTask={setEditingTaskID}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      {editingTask && (
        <TaskFormModal
          key={editingTask.taskID}
          task={editingTask}
          groups={groups
            .filter((g) => g.items.some((i) => i.taskID === editingTask.taskID))
            .map((g) => ({ taskGroupID: g.taskGroupID, name: g.name, active: g.active }))}
          onClose={() => setEditingTaskID(null)}
          onSaved={() => {
            setEditingTaskID(null);
            loadTasks();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${groupName(deleting)}"?` : ''}
        message="It will no longer add tasks. You can restore it from Show deleted."
        confirmLabel="Delete task group"
        variant="danger"
      />
    </AdminListPage>
  );
}
